import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonTitle,
  IonContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonLoading,
  useIonViewWillEnter,
  IonItem,
  IonLabel,
  IonText,
  IonSearchbar,
} from "@ionic/react";
import TestEnvBanner from "../components/TestEnvBanner";
import { useState, useContext, useCallback, useRef, useEffect } from "react";
import { useParams, useHistory } from "react-router";
import InspectionGroupList from "../components/InspectionGroupList";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import { chevronBackOutline, chevronBackSharp, add } from "ionicons/icons";
import { decodeParam, encodeParam } from "../util/ApiHelper";
import useGetWorkOrder from "../hooks/useGetWorkOrder";
import { useWorkOrderStore } from "../store/wo-order";
import { LinkedInspection } from "../api/WorkOrders";
import useGetWorkOrderControl from "../hooks/work-order/useGetWorkOrderControl";
import useGetLinkedInspection from "../hooks/work-order/useGetLinkedInspection";
import { useShallow } from "zustand/react/shallow";
import { useOfflineStore } from "../store/offline-mode";

type Status = "Completed" | "Not Complete" | "Failed";

type InspectionItem = {
  id: string;
  name: string;
  required: boolean;
  status: Status;
  group: "Pre Job" | "Inspection" | "Post Job";
  columns: any[];
  inspectionId: number;
  plant: string;
  inspectionType: string;
};

const MultipleInspections: React.FC = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const displayWo: string = decodeParam(workOrderId);
  const history = useHistory();
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { translations } = useContext(TranslationsContext);

  const { trigger } = useGetWorkOrder();
  const workOrder = useWorkOrderStore((state) =>
    state.getWorkOrderById(displayWo)
  );
  const linkedInspections = useWorkOrderStore(
    useShallow((state) => state.getLinkedInspectionsForWo(displayWo))
  );

  const isOffline = useOfflineStore((state) => state.isOffline);

  const { getWorkOrderControlData } = useGetWorkOrderControl();
  const { getLinkedInspection } = useGetLinkedInspection();

  const [preJobs, setPreJobs] = useState<InspectionItem[]>([]);
  const [jobs, setJobs] = useState<InspectionItem[]>([]);
  const [postJobs, setPostJobs] = useState<InspectionItem[]>([]);
  const [showInitialLoading, setShowInitialLoading] = useState(false);
  const [errorState, setErrorState] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const isFetchingRef = useRef(false);
  const previousWorkOrderRef = useRef<string>("");

  const currentDisplayWoRef = useRef<string>(displayWo);

  useEffect(() => {
    currentDisplayWoRef.current = displayWo;
  }, [displayWo]);

  const processInspections = useCallback((inspections: LinkedInspection[]) => {
    const result: InspectionItem[] = [];
    inspections.forEach((inspection) => {
      const inspectionId = inspection.InspectionId;
      const required = inspection.Required;

      let status: Status = "Not Complete";
      if (inspection.Status === "Completed-Success") {
        status = "Completed";
      } else if (inspection.Status === "Completed-Failed") {
        status = "Failed";
      }

      const description = inspection.Description;
      const plant = inspection.Plant;
      const inspectionType = inspection.InspectionType;

      const baseItem = {
        id: inspectionId.toString(),
        name: description,
        required,
        status,
        columns: [],
        inspectionId,
        plant,
        inspectionType,
      };

      if (inspection.PreInspection && inspection.PostInspection) {
        result.push({ ...baseItem, group: "Pre Job" });
        result.push({ ...baseItem, group: "Post Job" });
      } else if (inspection.PreInspection) {
        result.push({ ...baseItem, group: "Pre Job" });
      } else if (inspection.PostInspection) {
        result.push({ ...baseItem, group: "Post Job" });
      } else {
        result.push({ ...baseItem, group: "Inspection" });
      }
    });
    return result;
  }, []);

  useEffect(() => {
    if (linkedInspections?.length) {
      const result = processInspections(linkedInspections);
      setPreJobs(result.filter((r) => r.group === "Pre Job"));
      setJobs(result.filter((r) => r.group === "Inspection"));
      setPostJobs(result.filter((r) => r.group === "Post Job"));
    } else {
      console.warn("No inspections or error response:", linkedInspections);
      setPreJobs([]);
      setJobs([]);
      setPostJobs([]);
    }
  }, [linkedInspections, processInspections]);

  useEffect(() => {
    if (
      previousWorkOrderRef.current !== displayWo &&
      previousWorkOrderRef.current !== ""
    ) {
      setPreJobs([]);
      setJobs([]);
      setPostJobs([]);
      setErrorState("");
      isFetchingRef.current = false;
    }
    previousWorkOrderRef.current = displayWo;
  }, [displayWo]);

  const fetchWorkOrderControl = useCallback(async () => {
    if (isOffline) return; //already fetched and stored in memory for offline access
    const current = useWorkOrderStore.getState().getWorkOrderById(displayWo);
    if (!displayWo || !current) return;
    await getWorkOrderControlData(current);
  }, [displayWo, getWorkOrderControlData, isOffline]);

  useIonViewWillEnter(() => {
    (async () => {
      const freshDisplayWo = currentDisplayWoRef.current;
      if (previousWorkOrderRef.current !== freshDisplayWo) {
        isFetchingRef.current = false;
        setPreJobs([]);
        setJobs([]);
        setPostJobs([]);
        setErrorState("");
      }
      if (
        isFetchingRef.current &&
        previousWorkOrderRef.current === freshDisplayWo
      ) {
        return;
      }

      isFetchingRef.current = true;
      previousWorkOrderRef.current = freshDisplayWo;
      setShowInitialLoading(true);
      setErrorState("");

      try {
        if (freshDisplayWo) {
          let currentWorkOrder = useWorkOrderStore
            .getState()
            .getWorkOrderById(freshDisplayWo);
          if (!currentWorkOrder?.Equipment) {
            await trigger(freshDisplayWo);
          }
        }

        await fetchWorkOrderControl();
        if (linkedInspections === undefined)
          //not yet fetched
          await getLinkedInspection(freshDisplayWo);
      } catch (e) {
        console.error("Error loading inspections:", e);
        setErrorState(
          translatedMessages["ErrLoadingInspections"]?.MessageText ||
            translations["err_loading_inspections"] ||
            "Failed to load inspections"
        );
        setPreJobs([]);
        setJobs([]);
        setPostJobs([]);
      } finally {
        isFetchingRef.current = false;
        setShowInitialLoading(false);
      }
    })();
  });

  const matchesSearch = useCallback(
    (item: InspectionItem) => {
      const term = searchTerm.trim().toLowerCase();
      if (!term) return true;
      return (
        item.id.toLowerCase().includes(term) ||
        item.name.toLowerCase().includes(term) ||
        item.inspectionType.toLowerCase().includes(term)
      );
    },
    [searchTerm]
  );

  const filteredPreJobs = preJobs.filter(matchesSearch);
  const filteredJobs = jobs.filter(matchesSearch);
  const filteredPostJobs = postJobs.filter(matchesSearch);

  const hasInspections =
    filteredPreJobs.length > 0 ||
    filteredJobs.length > 0 ||
    filteredPostJobs.length > 0;
  const woMaxStatus: string =
    useWorkOrderStore((state) => state.woMaxStatus) ?? "";
  const inspectionAllowedFromStore: boolean =
    useWorkOrderStore((state) =>
      state.getIsInspectionAllowedForWo?.(displayWo)
    ) ?? false;

  const inspectionAllowedStrict = inspectionAllowedFromStore;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton
              fill="clear"
              onClick={() =>
                history.replace(`/workorder/${encodeParam(displayWo)}`)
              }
            >
              <IonIcon slot="icon-only" icon={chevronBackOutline} />
            </IonButton>
          </IonButtons>
          <IonTitle>
            {displayWo} -{" "}
            {translatedMessages["Inspections"]?.MessageText ||
              translations["lbl_inspections"] ||
              "Inspections"}
          </IonTitle>
        </IonToolbar>
        <TestEnvBanner />
      </IonHeader>

      <IonContent className="ion-padding">
        <IonSearchbar
          value={searchTerm}
          onIonChange={(e) => setSearchTerm(e.detail.value ?? "")}
          debounce={300}
          placeholder={
            translations["lbl_search_inspections"] || "Search inspections"
          }
        />

        {errorState && (
          <IonItem lines="none">
            <IonLabel>
              <IonText color="danger">{errorState}</IonText>
            </IonLabel>
          </IonItem>
        )}

        {!showInitialLoading && hasInspections && !errorState && (
          <>
            {filteredPreJobs.length > 0 && (
              <InspectionGroupList
                title={
                  translations["lbl_pre_job_inspections"] ||
                  "Pre Job Inspections"
                }
                inspections={filteredPreJobs}
                workOrderId={displayWo}
                inspectionAllowed={inspectionAllowedStrict}
                woMaxStatus={woMaxStatus}
              />
            )}
            {filteredJobs.length > 0 && (
              <InspectionGroupList
                title={translations["lbl_inspections"] || "Inspections"}
                inspections={filteredJobs}
                workOrderId={displayWo}
                inspectionAllowed={inspectionAllowedStrict}
                woMaxStatus={woMaxStatus}
              />
            )}
            {filteredPostJobs.length > 0 && (
              <InspectionGroupList
                title={
                  translations["lbl_post_job_inspections"] ||
                  "Post Job Inspections"
                }
                inspections={filteredPostJobs}
                workOrderId={displayWo}
                inspectionAllowed={inspectionAllowedStrict}
                woMaxStatus={woMaxStatus}
              />
            )}
          </>
        )}

        <IonFab slot="fixed" vertical="bottom" horizontal="end">
          <IonFabButton
            disabled={isOffline}
            onClick={() =>
              history.push(
                `/workorder/${encodeParam(displayWo)}/inspections/add`
              )
            }
          >
            <IonIcon icon={add} />
          </IonFabButton>
        </IonFab>

        {!showInitialLoading && !hasInspections && !errorState && (
          <IonItem lines="none">
            <IonLabel>
              <IonText>
                {translatedMessages["NoInspectionsExist"]?.MessageText ||
                  translations["msg_no_inspections_exist"] ||
                  "No inspections exist"}
              </IonText>
            </IonLabel>
          </IonItem>
        )}

        <IonLoading
          cssClass="my-custom-class"
          isOpen={showInitialLoading}
          onDidDismiss={() => setShowInitialLoading(false)}
        />
      </IonContent>
    </IonPage>
  );
};

export default MultipleInspections;
